import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
// import { useNavigate } from "react-router-dom";
import NavigationItem from "./ItemsSidebar";
import FeatherIcon from "feather-icons-react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import binoculars from "../../assets/svg/sidebar/binoculars.svg"
import collection from "../../assets/svg/sidebar/collection.svg"
import paySupplier from "../../assets/svg/sidebar/pay-supplier.svg"
import pinOff from "../../assets/svg/sidebar/pin-off.svg"
import pinOn from "../../assets/svg/sidebar/pin-on.svg"
import { useContext } from "react";
import { CompanyContext, UserContext } from "src/context";
import { Roles } from "src/utils/roles";
import { ACCOUNTING_ROUTE_PATHS, isAccountingEnabledByEnv } from "src/utils/accounting";
import { iconUrl, logoUrl } from "src/global";
import './NewSidebarAccordion.css'; // Add this import for the new CSS
import { useModulePermissions } from "src/hooks/useModulePermissions";

export const NewSidebar = ({ isVisible, setIsVisible, isFixed, setIsFixed }) => {
  const [hover, setHover] = useState(false);
  // const [isFixed, setIsFixed] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [expandedCategories, setExpandedCategories] = useState([]); // Track which categories are expanded
  // const [openSections, setOpenSections] = useState([]); // Track which sections are open
  const history = useHistory();
  const navRef = useRef(null);

  const { selectedCompany } = useContext(CompanyContext)
  const { roles } = useContext(UserContext)
  const { hasRouteAccess } = useModulePermissions();
  const { t } = useTranslation("sidebar");
  const isAdmin = Array.isArray(roles) && roles.includes(Roles.ADMIN);
  const itemLabel = (id) => t(`items.${id}`);
  const sectionLabel = (key) => t(`sections.${key}`);
  const _entry = process.env.REACT_APP_ENTRY

  const holdingCompanies = [
    '6760156cac2d65001cb6256b',
    '678fd7e47f10bc001df0f5be',
    '678ff012ce119d001c920368',
    '678ff0752ebe32001bc5cc9d',
    '678ff0d99c76ff001c1db195',
    '678ff113ce119d001c92a0b9'
  ]

  const handleClickOutside = (event) => {
    if (navRef.current && !navRef.current.contains(event.target)) {
      if (setIsVisible) {
        setIsVisible(false);
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleHover = (status) => {
    if (!isFixed) {
      setHover(status);
      document.body.style.overflow = status ? "hidden" : "auto";
    }
    // Cuando isFixed es true, no cambiar el estado hover para mantener el sidebar expandido
  };

  const toggleFixedSidebar = () => {
    setIsFixed((prevState) => {
      const newFixedState = !prevState;
      if (newFixedState) {
        // Si se está fijando, mantener expandido y restaurar el scroll
        setHover(true);
        document.body.style.overflow = "auto";
      } else {
        // Si se está desfijando, permitir que el hover controle el estado
        setHover(false);
        document.body.style.overflow = "auto";
      }
      return newFixedState;
    });
  };

  const _handleSectionClick = (section) => {
    setActiveSection(section === activeSection ? '' : section);
  };

  const handleCategoryClick = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleItemClick = (section, route, categoryId) => {
    setActiveSection(section);
    // Keep the parent category expanded
    if (categoryId && !expandedCategories.includes(categoryId)) {
      setExpandedCategories(prev => [...prev, categoryId]);
    }
    history.push(route);
  };

  const sidebarData = {
    pendiente: [
      {
        label: itemLabel("softlanding"),
        id: "softlanding",
        onClick: () => handleItemClick("softlanding", "/softlanding"),
        hasChildren: false,
        icon: "star",
        children: [],
      },
    ],
    general: [
      {
        label: itemLabel("dashboard"),
        id: "dashboard",
        onClick: () => handleItemClick("dashboard", "/dashboard"),
        hasChildren: false,
        icon: "home",
        children: [],
      },
      {
        label: itemLabel("reportes"),
        id: "reportes",
        onClick: () => handleCategoryClick("reportes"),
        hasChildren: true,
        icon: "bar-chart-2",
        children: [
          {
            label: itemLabel("reporteVentas"),
            id: "reporteVentas",
            onClick: () => handleItemClick("reporteVentas", "/reportes_ventas", "reportes"),
          },
          {
            label: itemLabel("reporteCompras"),
            id: "reporteCompras",
            onClick: () => handleItemClick("reporteCompras", "/reportes_compras", "reportes"),
          },
          {
            label: itemLabel("reporteCobranza"),
            id: "reporteCobranza",
            onClick: () => handleItemClick("reporteCobranza", "/reportes_cobranza", "reportes"),
          },
          {
            label: itemLabel("reporteProveedores"),
            id: "reporteProveedores",
            onClick: () => handleItemClick("reporteProveedores", "/reportes_proveedores", "reportes"),
          },
          {
            label: itemLabel("reportesPersonalizados"),
            id: "reportesPersonalizados",
            onClick: () => handleItemClick("reportesPersonalizados", "/reportes_personalizados", "reportes"),
          },
          ...(isAdmin
            ? [
                {
                  label: itemLabel("reporteRRHH"),
                  id: "reporteRRHH",
                  onClick: () => handleItemClick("reporteRRHH", "/reportes_rrhh", "reportes"),
                },
              ]
            : []),
        ],
      },
    ],
    finanzas: [
      {
        label: itemLabel("finanzas"),
        id: "finanzas",
        onClick: () => handleCategoryClick("finanzas"),
        hasChildren: true,
        icon: "dollar-sign",
        children: [
          {
            label: itemLabel("flujoCaja"),
            id: "flujoCaja",
            onClick: () => handleItemClick("flujoCaja", "/flujo_caja", "finanzas"),
          },
          {
            label: itemLabel("estadoResultado"),
            id: "estadoResultado",
            onClick: () => handleItemClick("estadoResultado", "/eerr", "finanzas"),
          },
          {
            label: itemLabel("eoaf"),
            id: "eoaf",
            onClick: () => handleItemClick("eoaf", "/eoaf", "finanzas"),
          },
          {
            label: itemLabel("prestamos"),
            id: "prestamos",
            onClick: () => handleItemClick("prestamos", "/creditos", "finanzas"),
          },
          /*
            Los tres de gestión van juntos y en Finanzas, no en Contabilidad:
            leen los mismos asientos pero agrupados por la estructura que arma
            el usuario, no por el plan de cuentas. La estructura maestra es
            justamente la herramienta con la que se arman esos dos reportes.
          */
          {
            label: itemLabel("estadoResultadosGestion"),
            id: "estadoResultadosGestion",
            onClick: () =>
              handleItemClick("estadoResultadosGestion", "/estado_de_resultados_gestion", "finanzas"),
          },
          {
            label: itemLabel("balanceClasificadoGestion"),
            id: "balanceClasificadoGestion",
            onClick: () =>
              handleItemClick("balanceClasificadoGestion", "/balance_clasificado_gestion", "finanzas"),
          },
          {
            label: itemLabel("estructurasMaestras"),
            id: "estructurasMaestras",
            onClick: () => handleItemClick("estructurasMaestras", "/estructuras_maestras", "finanzas"),
          },
        ],
      },
      // Documentos under Finanzas
      {
        label: itemLabel("documentos"),
        id: "documentos",
        onClick: () => handleCategoryClick("documentos"),
        hasChildren: true,
        icon: "file-text",
        children: [
          {
            label: itemLabel("dtes"),
            id: "dtes",
            onClick: () => handleItemClick("dtes", "/documentos_tributarios", "documentos"),
          },
          {
            label: itemLabel("boletas"),
            id: "boletas",
            onClick: () => handleItemClick("boletas", "/boletas", "documentos"),
          },
          {
            label: itemLabel("obligaciones"),
            id: "obligaciones",
            onClick: () => handleItemClick("obligaciones", "/obligaciones", "documentos"),
          },
          {
            label: itemLabel("cuotasDeCreditos"),
            id: "cuotasDeCreditos",
            onClick: () => handleItemClick("cuotasDeCreditos", "/cuotas_de_creditos", "documentos"),
          },
          {
            label: itemLabel("din"),
            id: "din",
            onClick: () => handleItemClick("din", "/declaraciones_de_ingresos", "documentos"),
          },
        ],
      },
    ],
    operaciones: [
      {
        label: itemLabel("operaciones"),
        id: "operaciones",
        onClick: () => handleCategoryClick("operaciones"),
        hasChildren: true,
        icon: "layers",
        children: [
          {
            label: itemLabel("conciliacion"),
            id: "conciliacion",
            onClick: () => handleItemClick("conciliacion", "/conciliacion", "operaciones"),
          },
          {
            label: itemLabel("cobranza"),
            id: "cobranza",
            onClick: () => handleItemClick("cobranza", "/cobranza", "operaciones"),
          },
          {
            label: itemLabel("pagoProveedores"),
            id: "pagoProveedores",
            onClick: () => handleItemClick("pagoProveedores", "/pago_proveedores", "operaciones"),
          },
          {
            label: itemLabel("cuadreCartolas"),
            id: "cuadreCartolas",
            onClick: () => handleItemClick("cuadreCartolas", "/cuadre_cartolas", "operaciones"),
          },
          {
            label: itemLabel("tarjetasCredito"),
            id: "tarjetasCredito",
            onClick: () => handleItemClick("tarjetasCredito", "/tarjetas_credito", "operaciones"),
          },
        ],
      },
      {
        label: itemLabel("impuestos"),
        id: "impuestos",
        onClick: () => handleCategoryClick("impuestos"),
        hasChildren: true,
        icon: "percent",
        children: [
          {
            label: itemLabel("formulario29"),
            id: "formulario29",
            onClick: () => handleItemClick("formulario29", "/formulario-29", "impuestos"),
          },
          {
            label: itemLabel("consolidadoF29Hub"),
            id: "consolidadoF29Hub",
            onClick: () =>
              handleItemClick(
                "consolidadoF29Hub",
                "/consolidado-f29?tab=consolidado",
                "impuestos"
              ),
          },
          {
            label: itemLabel("multasDelSii"),
            id: "multasDelSii",
            onClick: () =>
              handleItemClick(
                "multasDelSii",
                "/multas-sii",
                "impuestos"
              ),
          },
          {
            label: "Declaración de Renta",
            id: "renta",
            onClick: () => handleItemClick("renta", "/renta", "impuestos"),
          },
          {
            label: "Declaraciones Juradas",
            id: "estadoDj",
            onClick: () => handleItemClick("estadoDj", "/estado_dj", "impuestos"),
          },
        ],
      },
      // Contabilidad under Operaciones
      {
        label: itemLabel("contabilidad"),
        id: "contabilidad",
        onClick: () => handleCategoryClick("contabilidad"),
        hasChildren: true,
        icon: "book",
        children: [
          {
            label: itemLabel("balance8Columnas"),
            id: "balance8Columnas",
            onClick: () => handleItemClick("balance8Columnas", "/balance_8_columnas", "contabilidad"),
          },
          {
            label: itemLabel("balanceClasificado"),
            id: "balanceClasificado",
            onClick: () => handleItemClick("balanceClasificado", "/balance_clasificado", "contabilidad"),
          },
          {
            label: itemLabel("estadoResultadosContable"),
            id: "estadoResultadosContable",
            onClick: () => handleItemClick("estadoResultadosContable", "/estado_de_resultados_contable", "contabilidad"),
          },
          {
            label: itemLabel("libroMayor"),
            id: "libroMayor",
            onClick: () => handleItemClick("libroMayor", "/libro_mayor", "contabilidad"),
          },
          {
            label: itemLabel("libroDiario"),
            id: "libroDiario",
            onClick: () => handleItemClick("libroDiario", "/libro_diario", "contabilidad"),
          },
          {
            label: itemLabel("asientos"),
            id: "asientos",
            onClick: () => handleItemClick("asientos", "/asientos", "contabilidad"),
          },
          {
            label: itemLabel("planDeCuentas"),
            id: "planDeCuentas",
            onClick: () => handleItemClick("planDeCuentas", "/plan_de_cuentas", "contabilidad"),
          },
          {
            label: itemLabel("centralizacionContable"),
            id: "centralizacionContable",
            onClick: () => handleItemClick("centralizacionContable", "/centralizacion_contable", "contabilidad"),
          },
          {
            label: itemLabel("auditor"),
            id: "auditor",
            onClick: () => handleItemClick("auditor", "/libro-compra-venta", "contabilidad"),
          },
          {
            label: itemLabel("cierreAperturaContable"),
            id: "cierreAperturaContable",
            onClick: () => handleItemClick("cierreAperturaContable", "/cierre_apertura_contable", "contabilidad"),
          },
        ],
      },
    ],
    recursosHumanos: [
      {
        label: itemLabel("recursosHumanos"),
        id: "recursosHumanos",
        onClick: () => handleCategoryClick("recursosHumanos"),
        hasChildren: true,
        icon: "users",
        children: [
          {
            label: itemLabel("haberesVariables"),
            id: "haberesVariables",
            onClick: () => handleItemClick("haberesVariables", "/haberes_variables", "recursosHumanos"),
          },
          {
            label: itemLabel("validadorLiquidaciones"),
            id: "validadorLiquidaciones",
            onClick: () => handleItemClick("validadorLiquidaciones", "/validador_liquidaciones", "recursosHumanos"),
          },
          {
            label: itemLabel("cargarNuevoEmpleado"),
            id: "cargarNuevoEmpleado",
            onClick: () => handleItemClick("cargarNuevoEmpleado", "/cargar_nuevo_empleado", "recursosHumanos"),
          },
          {
            label: itemLabel("contratosProximosAVencer"),
            id: "contratosProximosAVencer",
            onClick: () => handleItemClick("contratosProximosAVencer", "/contratos_proximos_a_vencer", "recursosHumanos"),
          },
        ],
      },
    ],
    administracion: [
      {
        label: itemLabel("repository"),
        id: "repository",
        onClick: () => handleItemClick("repository", "/repositorio"),
        hasChildren: false,
        icon: "folder",
        children: [],
      },
      {
        label: itemLabel("seguimiento"),
        id: "seguimiento",
        onClick: () => handleCategoryClick("seguimiento"),
        hasChildren: true,
        icon: "clipboard",
        children: [
          {
            label: itemLabel("fichaCliente"),
            id: "fichaCliente",
            onClick: () => handleItemClick("fichaCliente", "/ficha_cliente", "seguimiento"),
          },
          {
            label: itemLabel("avanceContable"),
            id: "avanceContable",
            onClick: () => handleItemClick("avanceContable", "/avance_contable", "seguimiento"),
          },
        ],
      },
      {
        label: itemLabel("ajustes"),
        id: "ajustes",
        onClick: () => handleCategoryClick("ajustes"),
        hasChildren: true,
        icon: "settings",
        children: [
          {
            label: itemLabel("conexiones"),
            id: "conexiones",
            onClick: () => handleItemClick("conexiones", "/conexiones", "ajustes"),
          },
          {
            label: itemLabel("agenda"),
            id: "agenda",
            onClick: () => handleItemClick("agenda", "/agenda", "ajustes"),
          },
          {
            label: itemLabel("configuracion"),
            id: "configuracion",
            onClick: () => handleItemClick("configuracion", "/configuracion", "ajustes"),
          },
          {
            label: itemLabel("descargas"),
            id: "descargas",
            onClick: () => handleItemClick("descargas", "/descargas", "ajustes"),
          },
        ],
      },
    ],
    // Holding: [
    //   {
    //     label: "Holding",
    //     id: "holding",
    //     onClick: () => {
    //       const newTab = window.open(entry, "_blank");

    //       setTimeout(() => {
    //         const token = localStorage.getItem("token");
    //         const user = localStorage.getItem("user");
    //         const persistRoot = localStorage.getItem("persist:root");
    //         // const holdingCompanies =['680aebff7514ac4534be820e', '680aed687514ac4534be8631'] //HARDCODED HOLDING EN DEVELOP PARA TESTING!!

    //         newTab.postMessage(
    //           { type: "SET_TOKEN", token },
    //           entry
    //         );
    //         newTab.postMessage(
    //           { type: "SET_USER", user },
    //           entry
    //         );
    //         newTab.postMessage(
    //           { type: "SET_PERSIST_ROOT", persistRoot },
    //           entry
    //         );
    //         newTab.postMessage(
    //           { type: "HOLDING_COMPANIES", holdingCompanies },
    //           entry
    //         );
    //         newTab.postMessage({ type: "INIT_COMPLETE" }, entry);
    //         // newTab.postMessage(
    //         //   {
    //         //     type: "INIT_SESSION",
    //         //     payload: {
    //         //       token: localStorage.getItem("token"),
    //         //       user: localStorage.getItem("user"),
    //         //       persistRoot: localStorage.getItem("persist:root"),
    //         //     },
    //         //   },
    //         //   entry // Asegúrate de que "entry" sea la URL destino
    //         // );
    //       }, 1000);
    //     },
    //     hasChildren: false,
    //     icon: "",
    //     children: [],
    //     hasSvg: true,
    //     svg: Holding
    //   },
    // ]
  };

  const enhanceSidebarDataWithPath = (data) => {
    const newData = {};

    Object.entries(data).forEach(([section, items]) => {
      newData[section] = items.map(item => {
        const fakeLink = /"\/(.*?)"/.exec(item.onClick.toString());
        const path = fakeLink ? `/${fakeLink[1]}` : null;
        return { ...item, path };
      });
    });

    return newData;
  }

  const filterSidebarData = (data, { selectedCompany, roles }) => {
    const result = {};

    // Funcionalidades bloqueadas para esta empresa
    const blockedFeatures = selectedCompany?.blockedFeatures || [];

    const shouldInclude = (path, isHolding, isSoftlanding) => {
      // PRIMERO: Verificar permisos del admin panel
      // Si el usuario no tiene acceso a esta ruta, ocultarla
      if (path && !hasRouteAccess(path)) {
        return false;
      }

      if (isSoftlanding) {
        // hasAccess ya se evaluó arriba. Visible solo mientras el proceso no esté cerrado.
        const isCompleted = selectedCompany?.softlanding?.isCompleted;
        return isCompleted === false;
      }

      if (isHolding) {
        return holdingCompanies.includes(selectedCompany?.id);
      }

      // Cuadratura: feature interna restringida por empresa
      if (['/cuadratura'].includes(path)) {
        if (selectedCompany?.id !== '677d45356d129a001c2d0cbe') return false;
      }

      // Formulario 29 / Consolidado F29 / estado F29: formulario_29 y consolidado_f29 (staff por defecto).

      // Estado de Observación F29: solo ADMIN, COLABORADOR y SUPER_COLABORADOR
      if (['/estado-observacion'].includes(path)) {
        const allowedRoles = [Roles.ADMIN, Roles.COLABORADOR, Roles.SUPER_COLABORADOR];
        return roles && roles.some((r) => allowedRoles.includes(r));
      }

      // Repositorio: acceso solo por modulePermissions (hasRouteAccess arriba), sin plan.

      // Contabilidad: acceso por modulePermissions (hasRouteAccess arriba), no por rol.
      // Solo el flag de entorno puede apagar todas las rutas contables del build.
      if (ACCOUNTING_ROUTE_PATHS.includes(path) && !isAccountingEnabledByEnv()) {
        return false;
      }

      return true;
    };

    Object.entries(data).forEach(([section, items]) => {
      const filteredItems = items.filter(item => {
        // Contabilidad: sin flag de entorno no aparece; si el flag está on, se decide
        // por hijos con hasAccess (abajo: si no queda ninguno, se oculta el padre).
        if (item.id === 'contabilidad' && !isAccountingEnabledByEnv()) {
          return false;
        }
        return shouldInclude(item.path, item.id === 'holding', item.id === 'softlanding');
      });
      
      // También filtrar children según permisos del admin panel
      const itemsWithFilteredChildren = filteredItems.map(item => {
        if (item.children && item.children.length > 0) {
          // Extraer la ruta de cada child desde su onClick
          const filteredChildren = item.children.filter(child => {
            const childClickStr = child.onClick.toString();
            const childPathMatch = /"\/(.*?)"/.exec(childClickStr);
            const childPath = childPathMatch ? `/${childPathMatch[1]}` : null;
            
            // Cuadre Formulario 29: solo ADMIN, SUPER_COLABORADOR y COLABORADOR
            if (childPath === '/cuadre_formulario_29') {
              const hasCuadreF29Role = roles?.includes(Roles.ADMIN) ||
                roles?.includes(Roles.SUPER_COLABORADOR) ||
                roles?.includes(Roles.COLABORADOR);
              if (!hasCuadreF29Role) return false;
            }

            // Estado de Observación F29: solo ADMIN, COLABORADOR y SUPER_COLABORADOR
            if (childPath === '/estado-observacion') {
              const allowedRoles = [Roles.ADMIN, Roles.COLABORADOR, Roles.SUPER_COLABORADOR];
              if (!roles || !roles.some((r) => allowedRoles.includes(r))) return false;
            }

            // Descargas: staff por defecto; USER_SMARTCFO solo con grant explícito
            // del módulo 'descargas' (lo resuelve hasRouteAccess más abajo).

            // Verificar permiso del admin panel para el child
            if (childPath && !hasRouteAccess(childPath)) {
              return false;
            }
            
            // Plan-based visibility (e.g. conciliación bancaria only when plan includes "conciliacion")
            if (childPath && !shouldInclude(childPath, false, false)) {
              return false;
            }
            return true;
          });
          
          // Si no quedan children (p.ej. Contabilidad sin ningún módulo con hasAccess), ocultar el padre
          if (filteredChildren.length === 0) {
            return null;
          }
          
          return { ...item, children: filteredChildren };
        }
        return item;
      }).filter(item => item !== null);
      
      if (itemsWithFilteredChildren.length > 0) {
        result[section] = itemsWithFilteredChildren;
      }
    });

    return result;
  }

  const enhancedSidebar = enhanceSidebarDataWithPath(sidebarData);
  const filteredSidebar = filterSidebarData(enhancedSidebar, { selectedCompany, roles });

  // Repositorio y EOAF: panel admin (modulePermissions).

  // Open all sections by default on mount
  // useEffect(() => {
  //   setOpenSections(Object.keys(filteredSidebar).map((_, idx) => idx));
  // }, [Object.keys(filteredSidebar).length]);

  return (
    <div>
      {
        true
        &&
        <nav
          data-app-sidebar-nav
          className={`${(hover || isFixed) ? 'tw-w-[319px]' : 'tw-w-[64px]'} tw-fixed tw-top-0 tw-transition-all tw-duration-340 tw-h-screen tw-px-[12px] tw-hidden md:tw-block`}
          style={{ backgroundColor: '#2E3B47', borderRight: '1px solid rgba(255,255,255,0.07)', zIndex: 1030 }}
          onMouseEnter={() => handleHover(true)}
          onMouseLeave={() => handleHover(false)}
        >
          <div className="tw-overflow-hidden">
            <div
              className="tw-flex tw-items-center tw-justify-between tw-ps-[7px] tw-gap-[10px]"
              style={{ height: 'var(--app-header-height, 72px)' }}
            >
              <div className="tw-flex tw-items-center tw-gap-[7px]">
                { !(hover || isFixed) && <img
                  src={iconUrl}
                  alt={t("logoAlt")}
                  className={`tw-w-[32px] tw-h-[32px]`}
                  style={{ objectFit: "contain", objectPosition: "center" }}
                /> }
                {
                  (hover || isFixed) &&
                  <img
                    src={logoUrl}
                    alt={t("logoAlt")}
                    className="tw-h-[24px] tw-w-auto tw-max-w-[150px]"
                    style={{ objectFit: "contain", objectPosition: "left center" }}
                  />
                }
              </div>
              {
                (hover || isFixed) &&
                <>
                  <div
                    className='tw-w-[40px] tw-h-[40px] tw-rounded-full tw-cursor-pointer tw-flex tw-shrink-0'
                    style={{
                      backgroundColor: isFixed ? 'transparent' : 'rgba(126, 200, 192, 0.25)',
                      border: isFixed ? '1px solid var(--calipso)' : 'none',
                    }}
                    onClick={toggleFixedSidebar}
                  >
                    {isFixed ?
                      <img
                        src={pinOff}
                        alt=""
                        className="tw-w-[20px] tw-h-[20px] tw-justify-center tw-m-auto"
                        style={{ filter: 'brightness(0) invert(1)' }}
                      ></img>
                      :
                      <img
                        src={pinOn}
                        alt=""
                        className="tw-w-[20px] tw-h-[20px] tw-justify-center tw-m-auto"
                        style={{ filter: 'brightness(0) invert(1)' }}
                      ></img>
                    }
                  </div>
                </>
              }
            </div>

              <div
                className="tw-grid tw-pt-[12px] tw-overflow-y-auto tw-h-full tw-overflow-x-hidden"
                style={{
                  maxHeight: 'calc(100vh - var(--app-header-height, 72px) - 12px)',
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {Object.entries(filteredSidebar).map(([sectionKey, items], idx) => (
                  <div key={sectionKey} className="tw-grid tw-gap-[5px]">
                    <div
                      className="tw-w-[295px] tw-flex tw-items-center tw-pt-[12px] tw-pb-[4px] tw-overflow-hidden tw-transition-all tw-duration-300"
                      style={{
                        opacity: (hover || isFixed) ? 1 : 0,
                        maxHeight: (hover || isFixed) ? '40px' : '0px',
                        transform: (hover || isFixed) ? 'translateX(0)' : 'translateX(-8px)',
                      }}
                    >
                      <p style={{
                        fontSize: '9px',
                        fontWeight: 700,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: 'rgba(255, 255, 255, 0.25)',
                        paddingLeft: '12px',
                        margin: 0,
                      }}>
                        {sectionLabel(sectionKey)}
                      </p>
                    </div>
                    {/* Always show modules for all sections */}
                    <div className="tw-space-y-[4px]">
                      {items.map((item) => (
                        <NavigationItem
                          key={`${sectionKey}-${item.id}`}
                          title={sectionLabel(sectionKey)}
                          label={item.label}
                          active={activeSection}
                          id={item.id}
                          onClick={() => item.onClick()}
                          icon={item.icon}
                          hasChildren={item.hasChildren}
                          children={item.children}
                          hasSvg={item.hasSvg}
                          svg={item.svg}
                          hover={hover || isFixed}
                          expandedCategories={expandedCategories}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
          </div>
        </nav>
      }
    </div>
  );
};
